import { useState, useEffect } from 'react';
import { 
  Flex,
  View,
  Text,
  Heading,
  Button,
  Card,
  Alert,
  Loader
} from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { uploadData, downloadData, list } from 'aws-amplify/storage';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import awsConfig from '../aws-exports';

const CvAssistance = () => {
  // CV Analysis state
  const [selectedFile, setSelectedFile] = useState(null);
  const [isAnalysing, setIsAnalysing] = useState(false);
  
  // File Manager state
  const [files, setFiles] = useState([]);
  const [fileScores, setFileScores] = useState({});
  const [fileErrors, setFileErrors] = useState({});
  const [authStatus, setAuthStatus] = useState("checking");
  const [notification, setNotification] = useState({ show: false, message: '', type: 'info' });
  const [currentUserId, setCurrentUserId] = useState(null);
  
  // AI Feedback state
  const [cvFeedback, setCvFeedback] = useState(() => {
    return {
      structure: 'Your CV structure will be analysed...',
      content: 'Content quality assessment will appear here...',
      keywords: 'Industry keywords and ATS optimisation tips...',
      score: 0,
      fileName: null
    };
  });
  
  const API_NAME = 'ai_analysis';
  const CV_RESULTS_API_NAME = 'cv_analysis_results';
  const DELETE_API_NAME = 'delete_cv_analysis';
  
  // Function to format feedback text with proper line breaks
  const formatFeedbackText = (text) => {
    if (!text) return '';
    
    // First, handle different sentence endings
    let formatted = text
      .replace(/\. /g, '.\n\n')  // Period followed by space
      .replace(/\.\n/g, '.\n\n') // Period followed by newline
      .replace(/\n\n\n+/g, '\n\n') // Remove excessive newlines
      .trim();
    
    // Special handling for keywords section which might have different punctuation
    if (formatted.includes('keywords') || formatted.includes('Keywords')) {
      formatted = formatted
        .replace(/; /g, ';\n\n')  // Semicolon followed by space
        .replace(/: /g, ':\n\n')  // Colon followed by space (for lists)
        .replace(/\n\n\n+/g, '\n\n'); // Clean up excessive newlines
    }
    
    return formatted;
  };
  
  useEffect(() => {
    // Check authentication status first
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount; checkAuth closes over component scope
  
  // File Manager functions
  const showNotification = (message, type = 'info') => {
    setNotification({ show: true, message, type });
    
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'info' });
    }, 5000);
  };
  
  async function checkAuth() {
    try {
      // Verify the user is authenticated
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      console.log("Auth session:", session);
      console.log("Current user:", user);
      
      // Set the current user ID for user-specific data
      setCurrentUserId(user.userId);
      
      setAuthStatus("authenticated");
      listFiles();
    } catch (error) {
      console.error("Authentication error:", error);
      setAuthStatus("unauthenticated");
    }
  }
  
  // Function to fetch and load the latest analysis on page load
  const fetchLatestOnLoad = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      
      if (!idToken) return;
      
      const apiConfig = awsConfig.aws_cloud_logic_custom.find(
        api => api.name === CV_RESULTS_API_NAME
      );
      
      if (!apiConfig) return;
      
      // Get latest result from API
      const apiEndpoint = `${apiConfig.endpoint}/results?latest=true`;
      
      const response = await fetch(apiEndpoint, {
        method: 'GET',
        headers: {
          'Authorization': idToken.toString(),
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.results && data.results.score > 0) {
          const feedbackData = {
            structure: data.results.feedback?.structure || 'Your CV structure will be analysed...',
            content: data.results.feedback?.content || 'Content quality assessment will appear here...',
            keywords: data.results.feedback?.keywords || 'Industry keywords and ATS optimisation tips...',
            score: data.results.score || 0,
            fileName: data.results.fileName || null
          };
          
          setCvFeedback(feedbackData);
        }
      }
    } catch (error) {
      console.error('Error fetching latest analysis on load:', error);
    }
  };

  // Updated listFiles function to check feedback validity
  async function listFiles() {
    try {
      const result = await list({
        options: {
          accessLevel: 'private'
        }
      });

      console.log('Files listed:', result);
      const filesList = result.items || [];
      setFiles(filesList);
      
      // If no files, reset everything
      if (filesList.length === 0) {
        resetToDefaults();
        return;
      }
      
      // Fetch the latest analysis from API (this ensures we always show the most recent)
      await fetchLatestOnLoad();
      
    } catch (error) {
      console.error('Error listing files:', error);
      showNotification('Failed to refresh file list: ' + error.message, 'error');
    }
  }
  
  // CV Analysis handlers
  const validateFileType = (file) => {
    const acceptedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    const acceptedExtensions = ['.pdf', '.doc', '.docx'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!acceptedTypes.includes(file.type) && !acceptedExtensions.includes(fileExtension)) {
      showNotification('Invalid file type. Please upload a PDF or Word document (.pdf, .doc, .docx)', 'error');
      return false;
    }
    
    // Check file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      showNotification('File size exceeds 5MB limit. Please upload a smaller file.', 'error');
      return false;
    }
    
    return true;
  };
  
  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFileType(file)) {
        setSelectedFile(file);
      }
    }
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
    if (e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFileType(file)) {
        setSelectedFile(file);
      }
    }
  };
  
  const handleUploadAndAnalyse = async () => {
    if (!selectedFile) return;
    
    let fileUploaded = false;
    const fileName = selectedFile.name;
    
    try {
      setIsAnalysing(true);
      showNotification(`Uploading ${fileName}...`, 'info');
      
      // Get the identity ID and bucket name
      const session = await fetchAuthSession();
      const identityId = session.identityId;
      const bucketName = awsConfig.aws_user_files_s3_bucket;
      
      if (!identityId || !bucketName) {
        throw new Error('Unable to get S3 configuration');
      }
      
      // First upload the file to S3
      await uploadData({
        key: fileName,
        data: selectedFile,
        options: {
          accessLevel: 'private',
          contentType: selectedFile.type
        }
      });
      
      fileUploaded = true;
      
      // Construct the S3 URI
      const s3Key = `private/${identityId}/${fileName}`;
      const s3Uri = `s3://${bucketName}/${s3Key}`;
      
      console.log('File uploaded to:', s3Uri);
      showNotification(`${fileName} uploaded successfully. Analysing...`, 'info');

      // Save the S3 URI to localStorage for use in job compatibility checks
      localStorage.setItem(`cv_latest_s3uri_${currentUserId}`, s3Uri);
      
      // Wait a moment to ensure S3 consistency
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Now send S3 URI to Lambda for analysis
      await analyseCVWithAI(s3Uri, fileName, identityId);
      
      // Clear the selected file
      setSelectedFile(null);
      
      // Clear the file input
      const fileInput = document.getElementById('cv-file-upload');
      if (fileInput) fileInput.value = '';
      
      // Refresh the file list and scores
      await listFiles();
      
      // Only show success if we actually got a score
      if (cvFeedback.score > 0) {
        showNotification(`${fileName} uploaded and analysed successfully!`, 'success');
      }
    } catch (error) {
      console.error('Error:', error);
      
      if (!fileUploaded) {
        // Upload failed
        showNotification(`Failed to upload ${fileName}: ${error.message}`, 'error');
      } else {
        // Upload succeeded but analysis failed
        showNotification(`${fileName} uploaded successfully but analysis failed. Please try analysing again.`, 'error');
        
        // Track the analysis failure
        setFileErrors(prev => ({
          ...prev,
          [fileName]: 'Analysis failed - please try re-uploading'
        }));
        
        // Still refresh the file list since upload succeeded
        await listFiles();
      }
      
      // Reset feedback on error
      setCvFeedback({
        structure: 'Unable to analyse CV structure. Please try again.',
        content: 'Unable to analyse CV content. Please try again.',
        keywords: 'Unable to analyse keywords. Please try again.',
        score: 0,
        fileName: null
      });
    } finally {
      setIsAnalysing(false);
    }
  };
  
  const analyseCVWithAI = async (s3Uri, fileName, identityId) => {
    try {
      // Get the current auth session to extract the token
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      
      if (!idToken) {
        throw new Error('No authentication token available');
      }
      
      // Find the API endpoint for the API
      const apiConfig = awsConfig.aws_cloud_logic_custom.find(
        api => api.name === API_NAME
      );
      
      if (!apiConfig) {
        throw new Error(`API '${API_NAME}' not found in aws-exports.js configuration`);
      }
      
      const apiEndpoint = `${apiConfig.endpoint}/${API_NAME}`;
      
      // Parse the S3 URI to extract bucket and key
      // Format: s3://bucket-name/path/to/file
      const s3Match = s3Uri.match(/^s3:\/\/([^/]+)\/(.+)$/);
      if (!s3Match) {
        throw new Error('Invalid S3 URI format');
      }
      
      const bucketName = s3Match[1];
      const s3Key = s3Match[2];
      
      console.log('Sending S3 URI info to Lambda:', {
        s3Uri: s3Uri,
        bucketName: bucketName,
        s3Key: s3Key,
        fileName: fileName
      });
      
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Authorization': idToken.toString(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          s3Uri: s3Uri,
          bucketName: bucketName,
          s3Key: s3Key,
          fileName: fileName,
          identityId: identityId,
          caller: 'cv-analysis'
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Analysis failed with status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Parse the AI analysis response
      if (data.translated) {
        const feedback = parseCVFeedback(data.translated);
        feedback.fileName = fileName; // Add fileName to feedback
        setCvFeedback(feedback);
        
        // Update the file scores immediately
        setFileScores(prev => ({
          ...prev,
          [fileName]: feedback.score
        }));
        
        // Clear any previous error for this file
        setFileErrors(prev => {
          const newErrors = {...prev};
          delete newErrors[fileName];
          return newErrors;
        });
      }
      
    } catch (error) {
      console.error('CV Analysis error:', error);
      showNotification('Failed to analyse CV: ' + error.message, 'error');
      
      // Track the analysis failure for this file
      setFileErrors(prev => ({
        ...prev,
        [fileName]: 'Analysis failed - please try re-uploading'
      }));
      
      // Set default feedback on error
      setCvFeedback({
        structure: 'Unable to analyse CV structure at this time.',
        content: 'Unable to analyse CV content at this time.',
        keywords: 'Unable to analyse keywords at this time.',
        score: 0
      });
    }
  };
  
  const parseCVFeedback = (response) => {
    // Parse the response to extract different sections
    const feedback = {
      structure: 'No structure feedback available',
      content: 'No content feedback available',
      keywords: 'No keyword feedback available',
      score: 0,
      fileName: null
    };
    
    try {
      // Extract STRUCTURE section
      const structureMatch = response.match(/STRUCTURE:\s*(.+?)(?=\n\n|CONTENT:|$)/s);
      if (structureMatch) {
        feedback.structure = structureMatch[1].trim();
      }
      
      // Extract CONTENT section
      const contentMatch = response.match(/CONTENT:\s*(.+?)(?=\n\n|KEYWORDS:|$)/s);
      if (contentMatch) {
        feedback.content = contentMatch[1].trim();
      }
      
      // Extract KEYWORDS section
      const keywordsMatch = response.match(/KEYWORDS:\s*(.+?)(?=\n\n|SCORE:|$)/s);
      if (keywordsMatch) {
        feedback.keywords = keywordsMatch[1].trim();
      }
      
      // Extract SCORE
      const scoreMatch = response.match(/SCORE:\s*(\d+)/);
      if (scoreMatch) {
        feedback.score = parseInt(scoreMatch[1], 10);
      }
    } catch (error) {
      console.error('Error parsing CV feedback:', error);
    }
    
    return feedback;
  };
  
  const handleDownloadFile = async (fileName) => {
    try {
      console.log('Attempting to download file:', fileName);
      showNotification(`Downloading ${fileName}...`);
      
      // In Amplify v6, downloadData returns an object with a result property
      const downloadResult = await downloadData({
        key: fileName,
        options: {
          accessLevel: 'private'
        }
      }).result;
      
      console.log('Download completed, result:', downloadResult);
      
      // Create blob from the downloaded data
      const blob = new Blob([downloadResult.body], { type: downloadResult.contentType });
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showNotification(`${fileName} downloaded successfully!`, 'success');
    } catch (error) {
      console.error('Error downloading file:', error);
      showNotification('Download failed: ' + error.message, 'error');
    }
  };
  
  const handleDelete = async (fileName) => {
  try {
    const actualFileName = fileName.split('/').pop();
    showNotification(`Deleting ${actualFileName}...`);
    
    // Store whether we're deleting the current file
    const isDeletingCurrentFile = actualFileName === cvFeedback.fileName;
    
    // Get auth session for the delete API
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    const identityId = session.identityId;
    
    if (!idToken || !identityId) {
      throw new Error('Authentication required');
    }
    
    // Find the delete API endpoint
    const apiConfig = awsConfig.aws_cloud_logic_custom.find(
      api => api.name === DELETE_API_NAME
    );
    
    if (!apiConfig) {
      throw new Error(`API '${DELETE_API_NAME}' not found in configuration`);
    }
    
    const bucketName = awsConfig.aws_user_files_s3_bucket;
    
    // Call the delete Lambda - it handles both S3 and DynamoDB
    const deleteEndpoint = `${apiConfig.endpoint}/delete`;
    
    console.log('Calling delete Lambda with POST');
    
    const deleteResponse = await fetch(deleteEndpoint, {
      method: 'POST',
      headers: {
        'Authorization': idToken.toString(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fileName: actualFileName,
        identityId: identityId,
        bucketName: bucketName
      })
    });
    
    if (!deleteResponse.ok) {
      const errorData = await deleteResponse.json();
      throw new Error(errorData.error || 'Delete failed');
    }
    
    const deleteResult = await deleteResponse.json();
    console.log('Delete result:', deleteResult);
    
    // Get updated files list (but don't update state yet)
    const updatedFiles = files.filter(f => f.key !== fileName);
    
    // If no files will remain, reset everything
    if (updatedFiles.length === 0) {
      setFiles(updatedFiles);
      setFileErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[actualFileName];
        return newErrors;
      });
      setFileScores(prev => {
        const newScores = {...prev};
        delete newScores[actualFileName];
        return newScores;
      });
      
      if (currentUserId) {
        localStorage.removeItem(`cv_latest_s3uri_${currentUserId}`);
      }
      
      resetToDefaults();
      showNotification(`${actualFileName} deleted. No files remaining.`, 'success');
      return;
    }
    
    // If we're deleting the current file, load the next one FIRST
    if (isDeletingCurrentFile) {
      // Load next analysis before updating the file list
      const success = await loadMostRecentAvailableAnalysis(actualFileName, updatedFiles, 0);
      
      if (!success) {
        console.log('No analysis found for remaining files');
        resetToDefaults();
      }
    } else if (cvFeedback.score === 0) {
      // If no feedback is showing, try to load any available analysis
      await loadMostRecentAvailableAnalysis(null, updatedFiles, 0);
    }
    
    // NOW update the local state
    setFiles(updatedFiles);
    setFileErrors(prev => {
      const newErrors = {...prev};
      delete newErrors[actualFileName];
      return newErrors;
    });
    setFileScores(prev => {
      const newScores = {...prev};
      delete newScores[actualFileName];
      return newScores;
    });
    
    showNotification(`${actualFileName} deleted successfully!`, 'success');
    
  } catch (error) {
    console.error('Error in handleDelete:', error);
    showNotification('Delete failed: ' + error.message, 'error');
  }
};
  
const loadMostRecentAvailableAnalysis = async (excludeFileName = null, fileList = null, retryCount = 0) => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    
    // Use provided fileList or current files state
    const targetFiles = fileList || files;
    
    console.log(`Loading analysis (attempt ${retryCount + 1}) - excludeFileName:`, excludeFileName);
    console.log('Loading analysis - targetFiles:', targetFiles.map(f => f.key.split('/').pop()));
    
    if (!idToken || targetFiles.length === 0) {
      console.log('No token or no files');
      return false;
    }
    
    const apiConfig = awsConfig.aws_cloud_logic_custom.find(
      api => api.name === CV_RESULTS_API_NAME
    );
    
    if (!apiConfig) {
      console.log('No API config found');
      return false;
    }
    
    // Get all results from DynamoDB
    const apiEndpoint = `${apiConfig.endpoint}/results?limit=50`;
    console.log('Calling API endpoint:', apiEndpoint);
    
    const response = await fetch(apiEndpoint, {
      method: 'GET',
      headers: {
        'Authorization': idToken.toString(),
        'Content-Type': 'application/json',
      }
    });
    
    console.log('API Response status:', response.status);
    
    if (!response.ok) {
      console.log('API Response not ok');
      return false;
    }
    
    const data = await response.json();
    console.log('API Response data:', data);
    
    if (data.success && data.results && data.results.length > 0) {
      const results = Array.isArray(data.results) ? data.results : [data.results];
      console.log('All results from API:', results);
      
      // Get list of target file names
      const targetFileNames = targetFiles.map(f => f.key.split('/').pop());
      console.log('Target file names:', targetFileNames);
      
       // Filter to only valid results from target files, optionally excluding a specific file
      const validResults = results
        .filter(result => {
          const hasScore = result.score > 0;
          const isInTargetFiles = targetFileNames.includes(result.fileName);
          const notExcluded = excludeFileName ? result.fileName !== excludeFileName : true;
          const hasFeedback = !!result.feedback;
          
          console.log(`Filtering ${result.fileName}:`, {
            hasScore,
            isInTargetFiles,
            notExcluded,
            hasFeedback,
            score: result.score,
            targetFileNames,
            excludeFileName,
            passed: hasScore && isInTargetFiles && notExcluded && hasFeedback
          });
          
          return hasScore && isInTargetFiles && notExcluded && hasFeedback;
        })
        .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      
      console.log('Valid results after filtering:', validResults);
      
      validResults.forEach((result, index) => {
        console.log(`Result ${index + 1}:`, {
          fileName: result.fileName,
          score: result.score,
          timestamp: result.timestamp,
          timestampDate: new Date(result.timestamp),
          createdAt: result.createdAt
        });
      });

      if (validResults.length > 0) {
        const mostRecent = validResults[0];
        console.log('Most recent result:', mostRecent);
        
        const feedbackData = {
          structure: mostRecent.feedback?.structure || 'Your CV structure will be analysed...',
          content: mostRecent.feedback?.content || 'Content quality assessment will appear here...',
          keywords: mostRecent.feedback?.keywords || 'Industry keywords and ATS optimisation tips...',
          score: mostRecent.score,
          fileName: mostRecent.fileName
        };
        
        console.log('Setting feedback data:', feedbackData);
        setCvFeedback(feedbackData);

        return true;
      }
    }
    
    // If no results found and this is the first attempt, retry after a delay
    if (retryCount < 2) {
      console.log(`No results found, retrying in ${(retryCount + 1) * 1000}ms...`);
      await new Promise(resolve => setTimeout(resolve, (retryCount + 1) * 1000));
      return await loadMostRecentAvailableAnalysis(excludeFileName, fileList, retryCount + 1);
    }
    
    console.log('No valid results found after all retries');
    return false;
  } catch (error) {
    console.error('Error loading most recent analysis:', error);
    return false;
  }
};
  
  // Helper function to reset to defaults
  const resetToDefaults = () => {
    setCvFeedback({
      structure: 'Your CV structure will be analysed...',
      content: 'Content quality assessment will appear here...',
      keywords: 'Industry keywords and ATS optimisation tips...',
      score: 0,
      fileName: null
    });
    
    // Just emit the event, don't clear localStorage
    window.dispatchEvent(new Event('cvFeedbackUpdated'));
  };
  
  // Helper function to get score colour
  const getScoreColour = (score) => {
    if (score >= 80) return '#2ECC71';
    if (score >= 60) return '#F39C12';
    return '#E74C3C';
  };
  
  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score > 0) return 'Needs Work';
    return 'Not Analysed';
  };
  
  // Render notification alert
  const renderNotification = () => {
    if (!notification.show) return null;
    
    return (
      <Alert 
        variation={notification.type === 'error' ? 'error' : notification.type === 'success' ? 'success' : 'info'}
        isDismissible={true}
        onDismiss={() => setNotification({ show: false, message: '', type: 'info' })}
        marginBottom="1rem"
        style={{
          backgroundColor: notification.type === 'success' 
            ? 'rgba(46, 204, 113, 0.2)' 
            : notification.type === 'error'
            ? 'rgba(231, 76, 60, 0.2)'
            : 'rgba(52, 152, 219, 0.2)',
          border: `2px solid ${
            notification.type === 'success' 
              ? 'rgba(46, 204, 113, 0.5)' 
              : notification.type === 'error'
              ? 'rgba(231, 76, 60, 0.5)'
              : 'rgba(52, 152, 219, 0.5)'
          }`,
          color: 'white',
          borderRadius: '8px'
        }}
      >
        {notification.message}
      </Alert>
    );
  };

  if (authStatus === "checking") {
    return (
      <View style={{ position: 'relative', overflow: 'hidden' }}>        {/* Full page animated background */}
        <View
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            zIndex: -1
          }}
        />
        <View padding="2rem" style={{ position: 'relative', zIndex: 1 }}>
          <Card 
            borderRadius="12px" 
            padding="2rem"
            style={{
              background: 'rgba(30, 35, 45, 0.85)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
            }}
          >
            <Heading level={2} style={{ color: 'white' }}>CV Analysis</Heading>
            <Text color="rgba(255,255,255,0.8)">Checking authentication...</Text>
          </Card>
        </View>
      </View>
    );
  }

  if (authStatus === "unauthenticated") {
    return (
      <View style={{ position: 'relative', overflow: 'hidden', minHeight: '100vh' }}>
        {/* Full page animated background */}
        <View
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            zIndex: -1
          }}
        />
        <View padding="2rem" style={{ position: 'relative', zIndex: 1 }}>
          <Card 
            borderRadius="12px" 
            padding="2rem"
            style={{
              background: 'rgba(30, 35, 45, 0.85)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
            }}
          >
            <Heading level={2} style={{ color: 'white' }}>CV Analysis</Heading>
            <Text color="rgba(255,255,255,0.8)">Please sign in to access your files.</Text>
          </Card>
        </View>
      </View>
    );
  }
  
  return (
    <View style={{ position: 'relative', overflow: 'hidden', minHeight: '100vh' }}>
      {/* Full page animated background */}
      <View
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          zIndex: -1
        }}
      >
        {/* Animated bubbles covering entire background */}
        <View
          style={{
            position: 'absolute',
            top: '-100px',
            left: '-100px',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            animation: 'float 6s ease-in-out infinite'
          }}
        />
        <View
          style={{
            position: 'absolute',
            bottom: '-150px',
            right: '-150px',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
            animation: 'float 8s ease-in-out infinite reverse'
          }}
        />
        <View
          style={{
            position: 'absolute',
            top: '40%',
            left: '10%',
            width: '250px',
            height: '250px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.06)',
            animation: 'float 10s ease-in-out infinite'
          }}
        />
        <View
          style={{
            position: 'absolute',
            bottom: '30%',
            right: '20%',
            width: '350px',
            height: '350px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.07)',
            animation: 'float 12s ease-in-out infinite reverse'
          }}
        />
        <View
          style={{
            position: 'absolute',
            top: '60%',
            right: '40%',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.09)',
            animation: 'float 15s ease-in-out infinite'
          }}
        />
      </View>

      <View padding="2rem" style={{ position: 'relative', zIndex: 1 }}>
        {/* Notification area at the top */}
        {renderNotification()}
        
        {/* CV Analysis Section */}
        <Card 
          borderRadius="12px" 
          padding="2rem"
          style={{
            background: 'rgba(30, 35, 45, 0.85)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
          }}
        >
          <Flex direction="column" gap="1.5rem">
            <Heading level={2} style={{ color: 'white' }}>CV Analysis</Heading>
            <Text color="rgba(255,255,255,0.8)" fontSize="1.1rem">
              Upload your CV for AI-powered feedback and optimisation
            </Text>
            
            <Flex 
              direction={{ base: 'column', large: 'row' }}
              gap="2rem"
              marginTop="1rem"
            >
              {/* Upload Area */}
              <Flex direction="column" width={{ base: '100%', large: '50%' }} gap="1.5rem">
                <Flex
                  direction="column"
                  alignItems="center"
                  justifyContent="center"
                  border="2px dashed rgba(255,255,255,0.3)"
                  borderRadius="8px"
                  padding="3rem 1rem"
                  height="200px"
                  gap="1rem"
                  backgroundColor="rgba(255,255,255,0.05)"
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  style={{ cursor: 'pointer', transition: 'all 0.3s' }}
                  onClick={() => document.getElementById('cv-file-upload').click()}
                >
                  {!selectedFile ? (
                    <>
                      <View
                        backgroundColor="rgba(102, 126, 234, 0.15)"
                        padding="0.5rem 1rem"
                        borderRadius="8px"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        border="2px solid rgba(102, 126, 234, 0.3)"
                      >
                        <Text color="#667eea" fontSize="2rem" fontWeight="bold" style={{ lineHeight: '1', position: 'relative', top: '-3px' }}>+</Text>
                      </View>
                      <Text fontSize="1.1rem" fontWeight="bold" colour="white">Drag & Drop CV</Text>
                      <Text colour="rgba(255,255,255,0.7)">or click to browse</Text>
                    </>
                  ) : (
                    <>
                      <View
                        backgroundColor="rgba(102, 126, 234, 0.15)"
                        padding="0.5rem 1rem"
                        borderRadius="8px"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        border="2px solid rgba(102, 126, 234, 0.3)"
                      >
                        <Text color="#667eea" fontSize="2rem" fontWeight="bold" style={{ lineHeight: '1', position: 'relative', top: '-3px' }}>✓</Text>
                      </View>
                      <Text fontSize="1.1rem" fontWeight="bold" color="#667eea">File Selected</Text>
                      <Text color="rgba(255,255,255,0.7)">{selectedFile.name}</Text>
                      <Text color="rgba(255,255,255,0.6)" fontSize="0.9rem">
                        Click to change file
                      </Text>
                    </>
                  )}
                  <input
                    type="file"
                    id="cv-file-upload"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                </Flex>
                
                <Button
                  variation="primary"
                  size="large"
                  onClick={handleUploadAndAnalyse}
                  isDisabled={!selectedFile || isAnalysing}
                  isLoading={isAnalysing}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '1rem',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    color: 'white',
                    transition: 'all 0.3s',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    if (!e.target.disabled) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!e.target.disabled) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
                    }
                  }}
                >
                  Upload & Analyse
                </Button>
                
                <Text color="rgba(255,255,255,0.6)" fontSize="0.9rem">
                  Accepted formats: PDF, DOCX (Max 5MB)
                </Text>
                
                <Heading level={4} marginTop="1rem" style={{ color: 'white' }}>Your Files</Heading>
                
                {/* File list with scroll */}
                <View 
                style={{ 
                  height: files.length <= 3 ? 'auto' : undefined,
                  maxHeight: files.length > 3 ? 'calc(100vh - 600px)' : 'auto',
                  minHeight: '120px',
                  overflowY: files.length > 3 ? 'auto' : 'visible',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  backgroundColor: 'rgba(255,255,255,0.05)'
                }}
                >
                  {files.length === 0 ? (
                    <Card 
                      borderRadius="0" 
                      padding="1rem" 
                      backgroundColor="transparent" 
                      textAlign="center" 
                      border="none"
                    >
                      <Text color="rgba(255,255,255,0.6)">No files uploaded yet</Text>
                    </Card>
                  ) : (
                    <Flex direction="column">
                      {files
                        .sort((a, b) => {
                          // Sort by lastModified date in descending order (most recent first)
                          const dateA = a.lastModified ? new Date(a.lastModified).getTime() : 0;
                          const dateB = b.lastModified ? new Date(b.lastModified).getTime() : 0;
                          return dateB - dateA;
                        })
                        .map((file, index) => {
                        const uploadDate = file.lastModified ? new Date(file.lastModified) : null;
                        const formattedDate = uploadDate ? uploadDate.toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'Unknown';
                        
                        const fileName = file.key.split('/').pop();
                        const score = fileScores[fileName] || 0;
                        
                        return (
                          <Card 
                            key={file.key} 
                            borderRadius="0" 
                            padding="0.75rem 1rem" 
                            backgroundColor={index % 2 === 0 ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.07)"}
                            style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}
                          >
                            <Flex direction="column" gap="0.5rem">
                              <Flex justifyContent="space-between" alignItems="center" gap="0.5rem">
                                <Flex direction="column" style={{ flex: 1, overflow: 'hidden' }}>
                                  <Text fontWeight="bold" fontSize="0.9rem" color="white" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {fileName}
                                  </Text>
                                  <Text color="rgba(255,255,255,0.6)" fontSize="0.75rem">
                                    {formattedDate}
                                  </Text>
                                  {/* Error message aligned with filename and date */}
                                  {fileErrors[fileName] && (
                                    <Text 
                                      fontSize="0.8rem" 
                                      color="#E74C3C" 
                                      fontWeight="600" 
                                      marginTop="0.25rem"
                                    >
                                      {fileErrors[fileName]}
                                    </Text>
                                  )}
                                </Flex>
                                <Flex gap="0.25rem" style={{ flexShrink: 0 }}>
                                  <Button 
                                    size="small" 
                                    onClick={() => handleDownloadFile(file.key)}
                                    style={{
                                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                      border: 'none',
                                      color: 'white',
                                      padding: '0.25rem 0.75rem',
                                      fontSize: '0.875rem',
                                      borderRadius: '6px',
                                      fontWeight: '600',
                                      transition: 'all 0.3s',
                                      boxShadow: '0 3px 10px rgba(102, 126, 234, 0.3)'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.transform = 'translateY(-2px)';
                                      e.target.style.boxShadow = '0 5px 15px rgba(102, 126, 234, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.transform = 'translateY(0)';
                                      e.target.style.boxShadow = '0 3px 10px rgba(102, 126, 234, 0.3)';
                                    }}
                                  >
                                    Download
                                  </Button>
                                  <Button 
                                    size="small" 
                                    onClick={() => handleDelete(file.key)}
                                    style={{
                                      background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                                      border: 'none',
                                      color: 'white',
                                      padding: '0.25rem 0.75rem',
                                      fontSize: '0.875rem',
                                      borderRadius: '6px',
                                      fontWeight: '600',
                                      transition: 'all 0.3s',
                                      boxShadow: '0 3px 10px rgba(231, 76, 60, 0.3)'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.transform = 'translateY(-2px)';
                                      e.target.style.boxShadow = '0 5px 15px rgba(231, 76, 60, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.transform = 'translateY(0)';
                                      e.target.style.boxShadow = '0 3px 10px rgba(231, 76, 60, 0.3)';
                                    }}
                                  >
                                    Delete
                                  </Button>
                                </Flex>
                              </Flex>
                              
                              {/* Score tracker for each file */}
                              {score > 0 && (
                                <Flex alignItems="center" gap="0.5rem" marginTop="0.5rem">
                                  <Text fontSize="0.8rem" color="rgba(255,255,255,0.7)">ATS Score:</Text>
                                  <View style={{ flex: 1 }}>
                                    <View 
                                      backgroundColor="rgba(255,255,255,0.1)" 
                                      height="8px" 
                                      borderRadius="4px"
                                      overflow="hidden"
                                    >
                                      <View 
                                        backgroundColor={getScoreColour(score)}
                                        height="8px" 
                                        width={`${score}%`}
                                        style={{
                                          borderRadius: '4px',
                                          transition: 'width 0.5s ease-in-out'
                                        }}
                                      />
                                    </View>
                                  </View>
                                  <Text fontSize="0.8rem" fontWeight="bold" color="white">
                                    {score}%
                                  </Text>
                                  <View 
                                    backgroundColor={getScoreColour(score)}
                                    padding="0.1rem 0.5rem"
                                    borderRadius="12px"
                                  >
                                    <Text color="white" fontSize="0.7rem" fontWeight="bold">
                                      {getScoreLabel(score)}
                                    </Text>
                                  </View>
                                </Flex>
                              )}
                            </Flex>
                          </Card>
                        );
                      })}
                    </Flex>
                  )}
                </View>
              </Flex>
              
              {/* Feedback Area */}
              <Flex direction="column" width={{ base: '100%', large: '50%' }} gap="1.5rem">
                <Card 
                  borderRadius="12px" 
                  padding="2rem"
                  height="100%"
                  style={{
                    background: 'rgba(30, 35, 45, 0.85)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
                  }}
                >
                  <Flex direction="column" gap="1.5rem">
                    <Heading level={3} textAlign="center" style={{ color: 'white' }}>
                      AI Feedback
                      {isAnalysing && <Text fontSize="0.8rem" color="rgba(255,255,255,0.6)"> - Analysing...</Text>}
                    </Heading>
                    
                    {/* Updated Score Display like Homepage */}
                    <Card 
                      borderRadius="12px"
                      padding="1.5rem"
                      backgroundColor="rgba(255,255,255,0.05)"
                      style={{ 
                        boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255,255,255,0.1)'
                      }}
                    >
                      <Flex direction="column" gap="1rem">
                        <Flex justifyContent="space-between" alignItems="center">
                          <Text fontWeight="bold" fontSize="1.1rem" color="white">ATS Score</Text>
                          <View 
                            backgroundColor={cvFeedback.score > 0 ? getScoreColour(cvFeedback.score) : '#667eea'}
                            padding="0.25rem 0.75rem"
                            borderRadius="20px"
                          >
                            <Text color="white" fontSize="0.8rem" fontWeight="bold">
                              {getScoreLabel(cvFeedback.score)}
                            </Text>
                          </View>
                        </Flex>
                        
                        {isAnalysing ? (
                          <Flex justifyContent="center" alignItems="center" height="60px">
                            <Loader size="large" />
                          </Flex>
                        ) : (
                          <Flex direction="column" gap="0.5rem">
                            <View 
                              backgroundColor="rgba(255,255,255,0.1)" 
                              height="12px" 
                              borderRadius="6px"
                              overflow="hidden"
                            >
                              <View 
                                backgroundColor={cvFeedback.score > 0 ? getScoreColour(cvFeedback.score) : 'rgba(255,255,255,0.1)'}
                                height="12px" 
                                width={`${cvFeedback.score}%`}
                                style={{
                                  borderRadius: '6px',
                                  transition: 'width 0.5s ease-in-out'
                                }}
                              />
                            </View>
                            <Flex justifyContent="center" alignItems="baseline" gap="0.25rem">
                              <Text fontWeight="bold" fontSize="1.5rem" color="white">{cvFeedback.score}</Text>
                              <Text fontSize="1rem" color="rgba(255,255,255,0.7)">/100</Text>
                            </Flex>
                          </Flex>
                        )}
                        
                        {cvFeedback.fileName && cvFeedback.score > 0 && (
                          <Text fontSize="0.8rem" color="rgba(255,255,255,0.6)" textAlign="center" marginTop="0.5rem">
                            Currently showing: {cvFeedback.fileName}
                          </Text>
                        )}
                        
                        {(!cvFeedback.fileName || cvFeedback.score === 0) && (
                          <Text fontSize="0.8rem" color="rgba(255,255,255,0.6)" textAlign="center" marginTop="0.5rem">
                            No CV analysis available
                          </Text>
                        )}
                      </Flex>
                    </Card>
                    
                    {/* Structure Feedback - Styled like Homepage */}
                    <Card 
                      borderRadius="10px" 
                      padding="1.5rem" 
                      backgroundColor="rgba(52, 152, 219, 0.15)"
                      border="1px solid rgba(52, 152, 219, 0.3)"
                      style={{ transition: 'transform 0.2s' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateX(4px)';
                        e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.25)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateX(0)';
                        e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.15)';
                      }}
                    >
                      <Flex direction="column" gap="0.5rem">
                        <Flex alignItems="center" gap="0.5rem" justifyContent="center">
                          <Text fontSize="1.2rem">📋</Text>
                          <Text color="#67b8ff" fontWeight="bold">Structure:</Text>
                        </Flex>
                        <Text 
                          color="rgba(255,255,255,0.7)" 
                          fontSize="0.9rem" 
                          style={{ 
                            whiteSpace: 'pre-line',
                            textAlign: 'center',
                            lineHeight: '1.5'
                          }}
                        >
                          {formatFeedbackText(cvFeedback.structure)}
                        </Text>
                      </Flex>
                    </Card>
                    
                    {/* Content Feedback - Styled like Homepage */}
                    <Card 
                      borderRadius="10px" 
                      padding="1.5rem" 
                      backgroundColor="rgba(52, 152, 219, 0.15)"
                      border="1px solid rgba(52, 152, 219, 0.3)"
                      style={{ transition: 'transform 0.2s' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateX(4px)';
                        e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.25)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateX(0)';
                        e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.15)';
                      }}
                    >
                      <Flex direction="column" gap="0.5rem">
                        <Flex alignItems="center" gap="0.5rem" justifyContent="center">
                          <Text fontSize="1.2rem">📝</Text>
                          <Text color="#67b8ff" fontWeight="bold">Content:</Text>
                        </Flex>
                        <Text 
                          color="rgba(255,255,255,0.7)" 
                          fontSize="0.9rem" 
                          style={{ 
                            whiteSpace: 'pre-line',
                            textAlign: 'center',
                            lineHeight: '1.5'
                          }}
                        >
                          {formatFeedbackText(cvFeedback.content)}
                        </Text>                    
                      </Flex>
                    </Card>
                    
                    {/* Keywords Feedback - Styled like Homepage */}
                    <Card 
                      borderRadius="10px" 
                      padding="1.5rem" 
                      backgroundColor="rgba(52, 152, 219, 0.15)"
                      border="1px solid rgba(52, 152, 219, 0.3)"
                      style={{ transition: 'transform 0.2s' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateX(4px)';
                        e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.25)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateX(0)';
                        e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.15)';
                      }}
                    >
                      <Flex direction="column" gap="0.5rem">
                        <Flex alignItems="center" gap="0.5rem" justifyContent="center">
                          <Text fontSize="1.2rem">🔍</Text>
                          <Text color="#67b8ff" fontWeight="bold">Keywords:</Text>
                        </Flex>
                        <Text 
                          color="rgba(255,255,255,0.7)" 
                          fontSize="0.9rem" 
                          style={{ 
                            whiteSpace: 'pre-line',
                            textAlign: 'center',
                            lineHeight: '1.5'
                          }}
                        >
                          {formatFeedbackText(cvFeedback.keywords)}
                        </Text>                    
                      </Flex>
                    </Card>
                  </Flex>
                </Card>
              </Flex>
            </Flex>
          </Flex>
        </Card>
      </View>

      <style jsx>{`
        @keyframes float {
          0% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-30px);
          }
          100% {
            transform: translateY(0px);
          }
        }
      `}</style>
    </View>
  );
};

export default CvAssistance;